/* eslint-disable @angular-eslint/no-output-on-prefix */
import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { IMachineType } from 'projects/common/src/app/interfaces/machine-type';
import { IMachine } from 'src/app/modules/administration/interfaces/machine';
import { IZone } from 'src/app/modules/map/interfaces';
import { IFront } from 'src/app/modules/widget/interfaces/front';
import { IOperator } from 'src/app/modules/widget/interfaces/operator';
import { IShift } from '../../interfaces';
import { IFilterConfig } from '../../interfaces/filter-config';
import { IOutputFilter } from '../../interfaces/output-filter';
import { FilterHandlerService } from '../../services/filter/filter-handler.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'mt-filter',
  templateUrl: './mt-filter.component.html',
  styleUrls: ['./mt-filter.component.scss'],
})
export class MtFilterComponent implements AfterViewInit {
  @ViewChild('filtersContent', { static: false })
  public filtersContent: ElementRef<HTMLDivElement>;

  @Output() public filterChange: EventEmitter<IOutputFilter>;
  @Output() public onChangeDate: EventEmitter<any>;
  @Output() public onChangeFront: EventEmitter<any>;
  @Output() public onChangeMachine: EventEmitter<any>;
  @Output() public onChangeMachineType: EventEmitter<any>;
  @Output() public onChangeMine: EventEmitter<any>;
  @Output() public onChangeOperator: EventEmitter<any>;
  @Output() public onChangeShift: EventEmitter<any>;
  @Output() public onChangeZone: EventEmitter<any>;
  @Output() public onChangeAllMine: EventEmitter<any>;

  @Input() public filterConfig: IFilterConfig;

  @Input() public set progressBar(progressBar: boolean) {
    this.progressBarState = progressBar;
    this.updateFilterButtonState();
  }

  public filterForm: FormGroup;

  public machineType: IMachineType;
  public isCollapsed: boolean;
  public selectedFilter: IOutputFilter;
  public initialHeight: number;
  public progressBarState: boolean;
  public availableFronts: IFront[];

  private filterButtonDisabledSubject = new BehaviorSubject<boolean>(true);
  public filterButtonDisabled$ = this.filterButtonDisabledSubject.asObservable();

  constructor(private filterHandlerService: FilterHandlerService, private fb: FormBuilder) {
    this.isCollapsed = false;
    this.availableFronts = [];
    this.selectedFilter = {};
    this.filterChange = new EventEmitter<IOutputFilter>();
    this.progressBarState = false;

    this.onChangeDate = new EventEmitter<any>();
    this.onChangeFront = new EventEmitter<any>();
    this.onChangeMachine = new EventEmitter<any>();
    this.onChangeMachineType = new EventEmitter<any>();
    this.onChangeMine = new EventEmitter<any>();
    this.onChangeOperator = new EventEmitter<any>();
    this.onChangeShift = new EventEmitter<any>();
    this.onChangeZone = new EventEmitter<any>();
    this.onChangeAllMine = new EventEmitter<any>();

    this.filterForm = this.fb.group({
      startDate: [null, Validators.required],
      endDate: [null, Validators.required],
    });

    // Subscribe to form changes
    this.filterForm.valueChanges.subscribe(() => {
      this.updateFilterButtonState();
    });
  }

  /**
   * Function triggered after view init
   */
  public ngAfterViewInit(): void {
    setTimeout(() => {
      this.initialHeight = this.filtersContent.nativeElement.clientHeight;
    });
  }

  /**
   * Function to listen the window resize
   *
   * @param $event window resize event
   */
  @HostListener('window:resize', ['$event'])
  public onResize($event: any): void {
    this.initialHeight = $event.target.innerWidth < 1600 ? 300 : 220;

    if (!this.isCollapsed) {
      this.filtersContent.nativeElement.style.height = `${this.initialHeight}px`;
    }
  }

  /**
   * Function to clear all filters
   */
  public clearFilter(): void {
    const excludeKey = ['cleanButton', 'isLoading', 'filterButton'];

    this.filterHandlerService.clearFilter$.next();

    // Clear all available filters
    for (const key of Object.keys(this.filterConfig)) {
      if (!excludeKey.includes(key)) {
        this.filterConfig[key].value =
          this.filterConfig[key].uniqueSelection || this.filterConfig[key].isForMtv ? this.filterConfig[key].value : null;
      }
    }
    this.updateFilterButtonState();
  }

  /**
   * Function to filter
   */
  public filter(): void {
    this.filterChange.emit(this.selectedFilter);
  }

  /**
   * Function to toggle isCollapsed
   */
  public toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;

    if (this.isCollapsed) {
      this.filtersContent.nativeElement.style.height = '0px';
      this.filtersContent.nativeElement.style.overflow = 'hidden';
    } else {
      this.filtersContent.nativeElement.style.height = `${this.initialHeight}px`;
      setTimeout(() => {
        this.filtersContent.nativeElement.style.overflow = 'initial';
      }, 500);
    }
  }

  /**
   * Function triggered on date change
   */
  public changeDates($event: any): void {
    const fromDate = $event.from as NgbDate;
    const endDate = $event.to as NgbDate;

    this.filterForm.patchValue({
      startDate: fromDate || null,
      endDate: endDate || null,
    });

    let month = null;
    let day = null;

    if (endDate) {
      month = endDate.month < 10 ? `0${endDate.month}` : endDate.month;
      day = endDate.day < 10 ? `0${endDate.day}` : endDate.day;
      this.selectedFilter.endDate = `${endDate.year}/${month}/${day}`;
    }

    if (fromDate) {
      month = fromDate.month < 10 ? `0${fromDate.month}` : fromDate.month;
      day = fromDate.day < 10 ? `0${fromDate.day}` : fromDate.day;
      this.selectedFilter.startDate = `${fromDate.year}/${month}/${day}`;
    }

    this.filterConfig.dates.value = $event;
    this.updateFilterButtonState();
  }

  /**
   * Function to change filterConfig params by key
   *
   * @param $event new value
   * @param key: filter config key
   */
  public changeFilterParam(newValue: any, key: string, attr: string): void {
    if (Array.isArray(newValue)) {
      const values = newValue.map((v) => v[attr]);
      this.selectedFilter[attr] = values.join('|');
      this.filterConfig[key].value = newValue.length > 0 ? values.join('|') : null;
    } else {
      this.selectedFilter[attr] = newValue ? newValue[attr] : null;
      this.filterConfig[key].value = newValue ? newValue[attr] : null;
    }
    this.updateFilterButtonState();
  }

  /**
   * Function triggered on shift change
   */
  public changeShift($event: IShift): void {
    this.onChangeShift.emit($event);
    this.changeFilterParam($event, 'shift', 'shiftId');
  }

  /**
   * Function triggered on mine change
   */
  public changeMine($event: any): void {
    this.onChangeMine.emit($event);
    this.changeFilterParam($event, 'mine', 'zoneId');

    if (this.filterConfig?.machineList) {
      this.filterConfig.machineList.value = '';
    }

    if (this.filterConfig?.operator) {
      this.filterConfig.operator.value = '';
    }
    this.updateFilterButtonState();
  }

  /**
   * Function triggered on machine type change
   */
  public changeMachineType($event: IMachineType[]): void {
    if ($event?.length === 0) {
      this.filterConfig.machineList.value = '';
    }
    this.onChangeMachineType.emit($event);
    this.changeFilterParam($event, 'machineType', 'machineTypeId');
    if (this.filterConfig?.machineList) {
      this.filterConfig.machineList.value = '';
    }
    this.updateFilterButtonState();
  }

  /**
   * Function triggered on machine change
   */
  public changeMachine($event: IMachine[]): void {
    if ($event?.length === 0) {
      this.filterConfig.machineList.value = [];
    }

    this.onChangeMachine.emit($event);
    this.changeFilterParam($event, 'machineList', 'machineId');
  }

  /**
   * Function triggered on operator change
   */
  public changeOperator($event: IOperator[]): void {
    if ($event?.length === 0) {
      this.filterConfig.operator.value = [];
    }
    this.onChangeOperator.emit($event);
    this.changeFilterParam($event, 'operator', 'operatorId');
  }

  /**
   * Function triggered on zone change
   */
  public changeZone($event: IZone[]): void {
    this.onChangeZone.emit($event);
    this.changeFilterParam($event, 'zone', 'zoneId');
  }

  /**
   * Function triggered on front change
   */
  public changeFront($event: IFront[]): void {
    this.onChangeFront.emit($event);
    this.changeFilterParam($event, 'front', 'frontId');
  }
  /**
   * Function triggered on allMine change
   */
  public changeAllMine($event: IFront[]): void {
    this.onChangeAllMine.emit($event);
    this.changeFilterParam($event, 'allMine', 'zoneId');
  }

  /**
   * Function to check if the filter button is disabled
   */
  private updateFilterButtonState(): void {
    let isDisabled = false;

    if (!this.filterConfig) {
      // Prevents execution if filterConfig is not yet defined
      this.filterButtonDisabledSubject.next(true);
      return;
    }

    if (this.progressBarState) {
      isDisabled = true;
    } else {
      for (const key of Object.keys(this.filterConfig || {})) {
        if (this.filterConfig[key].required && !this.filterConfig[key].value) {
          isDisabled = true;
          break;
        }
      }
    }

    this.filterButtonDisabledSubject.next(isDisabled);
  }

  public get isFilterButtonDisabled(): boolean {
    return this.filterButtonDisabledSubject.value;
  }
}
